import { useEffect, useMemo, useState } from 'react';
import { queryClient } from 'Shared/queryClient';

import { RefetchFlags } from '../constants';
import useGetPatientData from './useGetPatientData';
import useGetPatientSubscriptions from './useGetPatientSubscriptions';
import useGetPatientTherapies from './useGetPatientTherapies';
import useGetPatientMedications from './useGetPatientMedications';
import usePatientEhr from './usePatientEhr';

/**
 * Custom hook which make all the http call to fetch details required to render patient detail page
 * @param {string} patientId
 * @returns Object containing profile data
 */
function usePatientProfile(patientId) {
    const { patientData, isPatientDataLoading } = useGetPatientData(patientId);
    const { patientMessagingSubscription, isSubscriptionLoading, triggerSubscriptionRefetch } =
        useGetPatientSubscriptions(patientId);
    const { therapies, isTherapyLoading, triggerTherapyRefetch } = useGetPatientTherapies(patientId);
    const { medications, isMedicationLoading, triggerMedicationRefetch } = useGetPatientMedications(patientId);
    const { ehr, isEhrLoading } = usePatientEhr(patientId);
    const [refetchFlag, setRefetchFlag] = useState(null);

    useEffect(() => {
        if (refetchFlag) {
            switch (refetchFlag) {
                case RefetchFlags.messagingSubscriptions:
                    triggerSubscriptionRefetch();
                    break;
                case RefetchFlags.unassignedMedications: {
                    triggerMedicationRefetch();
                    triggerTherapyRefetch();
                    queryClient.invalidateQueries(['patient', patientId, 'activities']);
                    queryClient.invalidateQueries(['patient', patientId, 'referrals']);
                    break;
                }
                default:
                // do nothing
            }
            setRefetchFlag(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchFlag]);

    const isProfileLoading = useMemo(() => {
        return isPatientDataLoading || isSubscriptionLoading || isTherapyLoading || isMedicationLoading || isEhrLoading;
    }, [isPatientDataLoading, isSubscriptionLoading, isTherapyLoading, isMedicationLoading, isEhrLoading]);

    return {
        isProfileLoading,
        isTherapyLoading,
        patientData,
        patientMessagingSubscription,
        therapies,
        medications,
        ehr,
        setRefetchFlag,
    };
}

export default usePatientProfile;
